import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { Calendar, DollarSign, Star, User, Home, Clock, MessageCircle, Phone, ChevronDown, ChevronUp, XCircle, Heart, FileText, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from "../components/Navbar";
import InvoiceModal from '../components/InvoiceModal';
import LoadingScreen from '../components/LoadingScreen';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  active: 'bg-teal-100 text-teal-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-gray-100 text-gray-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function ClientDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // ?booking={id} — usado por NotificationBell para llevar directo a la
  // reserva relevante en vez de abrir el dashboard genérico. Se sincroniza
  // en el useEffect de abajo (no como lazy initializer): si el usuario hace
  // clic en una notificación distinta sin recargar la página, el componente
  // no se remonta y un useState lazy quedaría pegado al primer booking.
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [cancellingId, setCancellingId] = useState(null);
  const [invoiceBooking, setInvoiceBooking] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  // Disputa — endpoint POST /bookings/{id}/dispute ya existe en el backend
  // (bookings.py:443). Aquí exponemos la UI para que el cliente la abra.
  const [disputingBooking, setDisputingBooking] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [verifyBannerClosed, setVerifyBannerClosed] = useState(false);
  const [loyalty, setLoyalty] = useState(null);

  const statusLabel = (status) => ({
    confirmed: t('clientDashboard.statusConfirmed'),
    pending: t('clientDashboard.statusPending'),
    active: t('clientDashboard.statusActive', 'En curso'),
    completed: t('clientDashboard.statusCompleted'),
    cancelled: t('clientDashboard.statusCancelled'),
    rejected: t('clientDashboard.statusRejected', 'Rechazada'),
  }[status] || status);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleCancel = async (bookingId) => {
    setCancelLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/cancel`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', t('clientDashboard.cancelledRefund', { percent: data.refund_percentage }));
        setCancellingId(null);
        setBookings(prev => prev.map(b => b.booking_id === bookingId ? {
          ...b,
          status: 'cancelled',
          // El endpoint de cancelar ya devuelve el reembolso -- sin esto,
          // el bloque de "Reembolso pendiente" no aparecia hasta recargar
          // la pagina (BUG-07).
          refund: data.refund_amount > 0 ? { status: 'pending', amount: data.refund_amount, refunded_at: null } : b.refund,
        } : b));
      } else {
        showToast('error', data.detail || t('clientDashboard.couldNotCancel'));
      }
    } catch {
      showToast('error', t('clientDashboard.connectionError'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleOpenDispute = async () => {
    if (!disputingBooking) return;
    const reason = disputeReason.trim();
    if (reason.length < 10) {
      showToast('error', t('clientDashboard.disputeReasonTooShort', 'El motivo debe tener al menos 10 caracteres.'));
      return;
    }
    setDisputeLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings/${disputingBooking.booking_id}/dispute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', t('clientDashboard.disputeOpened', 'Disputa abierta. Nuestro equipo la revisará en 24-48h.'));
        setDisputingBooking(null);
        setDisputeReason('');
      } else {
        showToast('error', data.detail || t('clientDashboard.disputeError', 'No se pudo abrir la disputa.'));
      }
    } catch {
      showToast('error', t('clientDashboard.connectionError'));
    } finally {
      setDisputeLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    const linkedBookingId = searchParams.get('booking');
    // Sincroniza expandedId aquí (no en useState lazy): si el usuario hace
    // clic en una segunda notificación sin recargar, este efecto se
    // re-dispara (searchParams cambia de identidad) y actualiza la card
    // expandida, cosa que un lazy initializer nunca haría tras el mount.
    setExpandedId(linkedBookingId);
    fetch(`${BACKEND_URL}/api/bookings`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setBookings(list);
        setLoading(false);
        // Si el booking enlazado desde la notificación está en el historial
        // (completado/cancelado/rechazado), cambia a esa tab automáticamente.
        if (linkedBookingId) {
          const linked = list.find(b => b.booking_id === linkedBookingId);
          if (linked && ['completed', 'cancelled', 'rejected'].includes(linked.status)) {
            setActiveTab('history');
          }
        }
      })
      .catch(() => { navigate('/login'); });
    fetch(`${BACKEND_URL}/api/client/loyalty`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(setLoyalty)
      .catch(() => {});
  }, [user, authLoading, navigate, searchParams]);

  if (loading || authLoading) {
    return <LoadingScreen />;
  }

  // 'active' = reserva en curso (empezó pero aún no terminó, por el scheduler).
  // 'rejected' = el proveedor rechazó la reserva (queda en el historial junto a
  // canceladas/completadas). Sin incluirlos aquí, quedaban invisibles al cliente.
  const activeBookings = bookings.filter(b => ['confirmed', 'pending', 'active'].includes(b.status));
  const historyBookings = bookings.filter(b => ['completed', 'cancelled', 'rejected'].includes(b.status));
  const displayedBookings = activeTab === 'active' ? activeBookings : historyBookings;

  const stats = {
    total: bookings.length,
    active: activeBookings.length,
    completed: historyBookings.filter(b => b.status === 'completed').length,
  };

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {user && !user.email_verified && !verifyBannerClosed && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="container mx-auto max-w-6xl flex items-start gap-3">
            <span className="text-blue-500 text-xl shrink-0">📧</span>
            <div className="flex-1">
              <p className="font-semibold text-blue-800 text-sm">{t('clientDashboard.verifyEmailTitle')}</p>
              <p className="text-blue-700 text-xs mt-0.5">{t('clientDashboard.verifyEmailDesc')}</p>
            </div>
            <button
              onClick={() => setVerifyBannerClosed(true)}
              aria-label={t('clientDashboard.closeNotice')}
              className="shrink-0 text-blue-500 hover:text-blue-800 transition-colors text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-2xl shadow-lg text-white font-medium text-sm
          ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-6xl py-8 pb-24 md:pb-8">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 mb-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#134168] rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0">
                {user?.name?.[0] || 'U'}
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#111827]">{user?.name}</h1>
                <p className="text-gray-500 text-sm">{user?.email}</p>
                <span className="mt-1 inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{t('clientDashboard.clientBadge')}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/client/profile')}
              className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
              title={t('clientDashboard.editProfile')}
            >
              <User className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </motion.div>

        {/* Banner de Verificación KYC */}
        {user && !user.verified && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-3xl p-5 mb-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="flex items-start sm:items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="font-bold text-amber-900 text-sm md:text-base">
                  {user.verification_status === 'pending'
                    ? t('clientDashboard.kycPendingTitle', 'Documento de identidad en revisión')
                    : t('clientDashboard.kycRequiredTitle', 'Verifica tu identidad (KYC)')}
                </h2>
                <p className="text-xs text-amber-700 mt-0.5">
                  {user.verification_status === 'pending'
                    ? t('clientDashboard.kycPendingDesc', 'Tu documento está siendo revisado por nuestro equipo de seguridad.')
                    : t('clientDashboard.kycRequiredDesc', 'Sube una foto de tu cédula o licencia para poder reservar vehículos sin esperas.')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/client/verificacion')}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-full font-bold text-xs md:text-sm hover:bg-amber-700 transition shrink-0 text-center"
            >
              {user.verification_status === 'pending' ? t('clientDashboard.viewKycStatus', 'Ver estado') : t('clientDashboard.verifyIdentityBtn', 'Subir documento')}
            </button>
          </motion.div>
        )}

        {loyalty && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[#134168] to-[#36C0C7] rounded-3xl p-6 mb-6 shadow-sm text-white"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="text-4xl">{loyalty.tier?.icon}</div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/70 font-bold">{t('loyalty.level', 'Nivel')}</p>
                  <p className="text-2xl font-heading font-bold">{loyalty.tier?.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{loyalty.total_points}</p>
                <p className="text-xs text-white/70">{t('loyalty.points', 'puntos')}</p>
              </div>
            </div>
            {loyalty.next_tier && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-white/80 mb-1">
                  <span>{t('loyalty.toNext', 'Para')} {loyalty.next_tier.icon} {loyalty.next_tier.name}</span>
                  <span>{loyalty.next_tier.points_needed} {t('loyalty.pointsLeft', 'pts restantes')}</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.round((loyalty.total_points / loyalty.next_tier.at_points) * 100))}%` }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 transition active:opacity-75 min-h-[44px]"
          >
            <Home className="w-4 h-4" /> {t('navbar.home')}
          </button>
          <button
            onClick={() => navigate('/client/favorites')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 transition active:opacity-75 min-h-[44px]"
          >
            <Heart className="w-4 h-4 text-red-500 fill-red-500" /> {t('clientDashboard.favorites', 'Favoritos')}
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition min-h-[44px] ${
              activeTab === 'active' ? 'bg-[#134168] text-white' : 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
            }`}
          >
            <Calendar className="w-4 h-4" /> {t('clientDashboard.tabActive')} {activeBookings.length > 0 && `(${activeBookings.length})`}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition min-h-[44px] ${
              activeTab === 'history' ? 'bg-[#134168] text-white' : 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
            }`}
          >
            <Clock className="w-4 h-4" /> {t('clientDashboard.tabHistory')}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { icon: Calendar, label: t('clientDashboard.stat_total'), value: stats.total, color: 'bg-blue-100 text-blue-600' },
            { icon: Star, label: t('clientDashboard.stat_active'), value: stats.active, color: 'bg-green-100 text-green-600' },
            { icon: DollarSign, label: t('clientDashboard.stat_completed'), value: stats.completed, color: 'bg-purple-100 text-purple-600' },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.08 }}
              className="bg-white rounded-2xl p-4 shadow-sm"
            >
              <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center mb-2`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-5">
            {activeTab === 'active' ? t('clientDashboard.activeBookings') : t('clientDashboard.historyTitle')}
          </h2>

          {displayedBookings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                {activeTab === 'active' ? t('clientDashboard.noActiveBookings') : t('clientDashboard.noHistory')}
              </p>
              <button onClick={() => navigate('/')} className="bg-[#134168] text-white px-6 py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-all">
                {t('clientDashboard.exploreVehicles')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {displayedBookings.map(booking => (
                <div key={booking.booking_id} className="border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md transition-all">
                  <button
                    onClick={() => setExpandedId(expandedId === booking.booking_id ? null : booking.booking_id)}
                    className="w-full text-left p-4 active:opacity-75"
                  >
                    <div className="flex items-start gap-4">
                      <img
                        src={booking.asset?.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                        alt={booking.asset?.name}
                        className="w-20 h-16 rounded-xl object-cover shrink-0 bg-gray-100"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-bold text-sm leading-tight truncate">{booking.asset?.name || t('bookingConfirmation.vehicle')}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${STATUS_COLORS[booking.status] || 'bg-gray-100 text-gray-700'}`}>
                            {statusLabel(booking.status)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 font-mono mb-1">{booking.booking_code}</p>
                        <p className="text-xs text-gray-600">
                          {new Date(booking.start_datetime).toLocaleDateString(locale)} · {booking.end_hours}h
                        </p>
                        <p className="text-sm font-bold text-[#134168] mt-1">{formatDOP(booking.total_price)}</p>
                      </div>
                      <div className="shrink-0 self-center">
                        {expandedId === booking.booking_id
                          ? <ChevronUp className="w-5 h-5 text-gray-400" />
                          : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {expandedId === booking.booking_id && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">{t('clientDashboard.insurance')}</p>
                          <p className="font-medium capitalize">{booking.insurance_type || t('clientDashboard.insuranceNone2')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">{t('clientDashboard.platformFee')}</p>
                          <p className="font-medium">{formatDOP(booking.platform_commission)}</p>
                        </div>
                      </div>

                      {booking.dispute && (
                        <div className={`rounded-xl px-4 py-3 text-sm ${booking.dispute.status === 'resolved' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                          <p className="font-semibold">
                            {booking.dispute.status === 'resolved'
                              ? t('clientDashboard.disputeResolvedTitle', 'Disputa resuelta')
                              : t('clientDashboard.disputeOpenTitle', 'Disputa en revisión')}
                          </p>
                          {booking.dispute.status === 'resolved' ? (
                            <p>{booking.dispute.resolution || t('clientDashboard.disputeResolvedNoNotes', 'Resuelta por nuestro equipo.')}</p>
                          ) : (
                            <p>{t('clientDashboard.disputeOpenDesc', 'Nuestro equipo la está revisando, te avisaremos por aquí cuando haya una resolución.')}</p>
                          )}
                        </div>
                      )}

                      {booking.refund && (
                        <div className={`rounded-xl px-4 py-3 text-sm ${booking.refund.status === 'processed' ? 'bg-green-50 text-green-800' : booking.refund.status === 'failed' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
                          <p className="font-semibold">
                            {booking.refund.status === 'processed'
                              ? t('clientDashboard.refundProcessedTitle', 'Reembolso procesado')
                              : booking.refund.status === 'failed'
                              ? t('clientDashboard.refundFailedTitle', 'Reembolso con problema')
                              : t('clientDashboard.refundPendingTitle', 'Reembolso pendiente')}
                          </p>
                          <p>
                            {formatDOP(booking.refund.amount)}
                            {booking.refund.status !== 'processed' && ` · ${t('clientDashboard.refundPendingDesc', 'nuestro equipo lo procesa en los próximos días')}`}
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <button
                            onClick={() => setCancellingId(booking.booking_id)}
                            className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 rounded-full text-sm font-medium hover:bg-red-50 transition"
                          >
                            <XCircle className="w-4 h-4" /> {t('clientDashboard.cancelBookingBtn')}
                          </button>
                        )}
                        {booking.status === 'completed' && (
                          <button
                            onClick={() => navigate(`/client/review/${booking.booking_id}`)}
                            className="flex items-center gap-1.5 px-4 py-2 bg-yellow-400 text-yellow-900 rounded-full text-sm font-bold hover:bg-yellow-500 transition"
                          >
                            <Star className="w-4 h-4" /> {t('clientDashboard.leaveReviewBtn')}
                          </button>
                        )}
                        {(booking.status === 'confirmed' || booking.status === 'completed') && (
                          <button
                            onClick={() => setInvoiceBooking(booking)}
                            className="flex items-center gap-1.5 px-4 py-2 border border-[#134168]/30 text-[#134168] rounded-full text-sm font-medium hover:bg-blue-50 transition"
                          >
                            <FileText className="w-4 h-4" /> {t('invoice.viewInvoice')}
                          </button>
                        )}
                        {(booking.status === 'confirmed' || booking.status === 'active' || booking.status === 'completed') && !(booking.dispute && booking.dispute.status !== 'resolved') && (
                          <button
                            onClick={() => { setDisputingBooking(booking); setDisputeReason(''); }}
                            className="flex items-center gap-1.5 px-4 py-2 border border-amber-300 text-amber-700 rounded-full text-sm font-medium hover:bg-amber-50 transition"
                          >
                            <AlertTriangle className="w-4 h-4" /> {t('clientDashboard.openDisputeBtn', 'Abrir disputa')}
                          </button>
                        )}
                      </div>

                      {booking.provider && (
                        <div className="bg-white border border-gray-200 rounded-xl p-3">
                          <p className="text-xs font-bold text-gray-700 mb-2">{t('clientDashboard.providerContact')}</p>
                          <p className="text-sm font-medium">{booking.provider.name}</p>
                          {booking.provider.phone_whatsapp && (
                            <a
                              href={`https://wa.me/${booking.provider.phone_whatsapp.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 flex items-center gap-2 bg-green-500 text-white px-3 py-2 rounded-xl text-sm font-medium hover:bg-green-600 transition w-full justify-center min-h-[44px]"
                            >
                              <MessageCircle className="w-4 h-4" /> {t('clientDashboard.whatsappToProvider')}
                            </a>
                          )}
                          {booking.provider.email && (
                            <a
                              href={`mailto:${booking.provider.email}`}
                              className="mt-2 flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 transition w-full justify-center min-h-[44px]"
                            >
                              <Phone className="w-4 h-4" /> {t('clientDashboard.emailToProvider')}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {invoiceBooking && (
        <InvoiceModal
          booking={invoiceBooking}
          user={user}
          onClose={() => setInvoiceBooking(null)}
        />
      )}

      {cancellingId && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('clientDashboard.cancelConfirmTitle2')}</h3>
            <p className="text-gray-500 text-sm mb-2">{t('clientDashboard.refundPolicy')}</p>
            <ul className="text-sm text-gray-600 text-left mb-6 space-y-1 bg-gray-50 rounded-xl p-3">
              <li><Trans i18nKey="clientDashboard.refund100" /></li>
              <li><Trans i18nKey="clientDashboard.refund50" /></li>
              <li><Trans i18nKey="clientDashboard.refund0" /></li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setCancellingId(null)}
                disabled={cancelLoading}
                className="flex-1 border border-gray-200 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-50 transition"
              >
                {t('clientDashboard.back2')}
              </button>
              <button
                onClick={() => handleCancel(cancellingId)}
                disabled={cancelLoading}
                className="flex-1 bg-red-500 text-white py-3 rounded-full font-bold hover:bg-red-600 transition disabled:opacity-50"
              >
                {cancelLoading ? t('clientDashboard.cancelling') : t('clientDashboard.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {disputingBooking && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-center">{t('clientDashboard.disputeTitle', 'Abrir disputa')}</h3>
            <p className="text-gray-500 text-sm mb-4 text-center">
              {t('clientDashboard.disputeIntro', 'Describe qué pasó con esta reserva. Nuestro equipo lo revisa en 24-48h y contacta al proveedor.')}
            </p>
            <p className="text-xs text-gray-400 mb-4 text-center">
              {t('clientDashboard.disputeCode', 'Reserva')}: <span className="font-mono">{disputingBooking.booking_code}</span>
            </p>
            <textarea
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              placeholder={t('clientDashboard.disputeReasonPlaceholder', 'Ej. el vehículo llegó dañado, faltaron accesorios, no se cumplió con el horario...')}
              rows={5}
              className="w-full border border-gray-200 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 mb-4">{disputeReason.length} / 500 · {t('clientDashboard.disputeMinChars', 'mínimo 10 caracteres')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setDisputingBooking(null); setDisputeReason(''); }}
                disabled={disputeLoading}
                className="flex-1 border border-gray-200 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-50 transition"
              >
                {t('clientDashboard.back2')}
              </button>
              <button
                onClick={handleOpenDispute}
                disabled={disputeLoading || disputeReason.trim().length < 10 || disputeReason.length > 500}
                className="flex-1 bg-amber-600 text-white py-3 rounded-full font-bold hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {disputeLoading ? t('clientDashboard.sending', 'Enviando...') : t('clientDashboard.disputeSend', 'Abrir disputa')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
